import { ValidationError } from '@/services/types';

export enum ServiceErrorCode {
  AiExperimentGenerationFailed = 'AI_EXPERIMENT_GENERATION_FAILED',
  AiExperimentImageGetFailed = 'AI_EXPERIMENT_IMAGE_GET_FAILED',
  AiExperimentParseFailure = 'AI_EXPERIMENT_PARSE_FAILURE',
  AiExperimentNoChoices = 'AI_EXPERIMENT_NO_CHOICES',
  AiExperimentNoMessageContent = 'AI_EXPERIMENT_NO_MESSAGE_CONTENT',
  AiExperimentUnknownMetricType = 'AI_EXPERIMENT_UNKNOWN_METRIC_TYPE',
  AiExperimentNotFound = 'AI_EXPERIMENT_NOT_FOUND',
  AmqpParseError = 'AMQP_PARSE_ERROR',
  AmqpHandlerError = 'AMQP_HANDLER_ERROR',
  AmqpConnectionError = 'AMQP_CONNECTION_ERROR',
  AmqpInvalidMessagePayload = 'AMQP_INVALID_MESSAGE_PAYLOAD',
  AmqpUnknownError = 'AMQP_UNKNOWN_ERROR',
  AlreadyJoined = 'ALREADY_JOINED',
  AlreadyJoinedWaitlist = 'ALREADY_JOINED_WAITLIST',
  AlreadyVerified = 'ALREADY_VERIFIED',
  BadRequest = 'BAD_REQUEST',
  CliMissingChallengeCode = 'CLI_MISSING_CHALLENGE_CODE',
  CliCodeNotFound = 'CLI_CODE_NOT_FOUND',
  CliPKCEMismatch = 'CLI_PKCE_MISMATCH',
  CliSessionNotLinked = 'CLI_SESSION_NOT_LINKED',
  CloudflareTurnstile = 'CloudflareTurnstile',
  CloudflareTurnstileHttpFailure = 'CloudflareTurnstileHttpFailure',
  CloudflareTurnstileMissing = 'CloudflareTurnstileMissing',
  InsertFailure = 'INSERT_FAILURE',
  BulkInsertFailure = 'BULK_INSERT_FAILURE',
  DuplicateEmail = 'DUPLICATE_EMAIL',
  DuplicateMetricName = 'DUPLICATE_METRIC_NAME',
  EmailInviteMismatch = 'EMAIL_INVITE_MISMATCH',
  FlagNotFound = 'FLAG_NOT_FOUND',
  FlagHasExperiments = 'FLAG_HAS_EXPERIMENTS',
  FlagsNoStates = 'FLAG_NO_STATES',
  InvalidToken = 'INVALID_TOKEN',
  IllegalTierAction = 'ILLEGAL_TIER_ACTION',
  EmptyTokens = 'EMPTY_TOKENS',
  EnvironmentNotFound = 'ENVIRONMENT_NOT_FOUND',
  EnvironmentRunningExperiments = 'ENVIRONMENT_RUNNING_EXPERIMENTS',
  ExperimentRunning = 'EXPERIMENT_RUNNING',
  InvalidCredentials = 'INVALID_CREDENTIALS',
  InvalidTimeframe = 'INVALID_TIMEFRAME',
  OAuthAccessDenied = 'OAUTH_ACCESS_DENIED',
  OrganizationNotFound = 'ORGANIZATION_NOT_FOUND',
  ForbiddenResource = 'FORBIDDEN_RESOURCE',
  MetricHasExperiments = 'METRIC_HAS_EXPERIMENTS',
  MongooseError = 'MONGOOSE_ERROR',
  MongooseNotFoundOrUnAuthorized = 'MONGOOSE_NOT_FOUND_OR_UNAUTHORIZED',
  PasswordReuse = 'PASSWORD_REUSE',
  StripeWebhookError = 'STRIPE_WEBHOOK_ERROR',
  StripeBillingSessionError = 'STRIPE_BILLING_SESSION_ERROR',
  StripePlanAlreadySelected = 'STRIPE_PLAN_ALREADY_SELECTED',
  StripeMissingPrice = 'STRIPE_MISSING_PRICE',
  ScreenshotCaptureFailed = 'SCREENSHOT_CAPTURE_FAILED',
  ScreenshotBodyTagNotFound = 'SCREENSHOT_BODY_TAG_NOT_FOUND',
  ScreenshotBoundingBoxFound = 'SCREENSHOT_BOUNDING_BOX_FOUND',
  TransactionFailure = 'TRANSACTION_FAILURE',
  TooManyExperimentIterations = 'TOO_MANY_EXPERIMENT_ITERATIONS',
  UnknownAccount = 'UNKNOWN_ACCOUNT',
  UnknownError = 'UNKNOWN_ERROR',
  UnknownEntity = 'UNKNOWN_ENTITY',
  UnknownExperimentIteration = 'UNKNOWN_EXPERIMENT_ITERATION',
  UserOrganizationNotFound = 'USER_ORGANIZATION_NOT_FOUND',
  UserOrganizationCtxNotFound = 'USER_ORGANIZATION_CTX_NOT_FOUND',
  UniqueViolationError = 'UNIQUE_VIOLATION_ERROR',
  ValidationGuard = 'VALIDATION_GUARD',
  InvalidUrl = 'INVALID_URL',
  UnsupportedProtocol = 'UNSUPPORTED_PROTOCOL',
  ForbiddenUrl = 'FORBIDDEN_URL',
  InvalidFileType = 'INVALID_FILE_TYPE',
  FileSizeExceeded = 'FILE_SIZE_EXCEEDED',
  CsrfSecretNotConfigured = 'CSRF_SECRET_NOT_CONFIGURED',
  CsrfTokenMismatch = 'CSRF_TOKEN_MISMATCH',
  ViewAsReadOnly = 'VIEW_AS_READ_ONLY',
  ViewAsTargetIsGod = 'VIEW_AS_TARGET_IS_GOD',
  ViewAsSelf = 'VIEW_AS_SELF',
}

interface ServiceErrorInput {
  fnName?: string;
  message?: string;
  errorCode?: string;
  validationErrors?: ValidationError[];
  metadata?: Record<string, any>;
}

export interface PropertyValidation {
  email?: boolean;
  password?: boolean;
  username?: boolean;
}

type PropertyValidationKey = keyof PropertyValidation;

export class ServiceError extends Error {
  public errorCode: ServiceErrorCode;
  public fnName: string;
  public message: string;
  public validationErrors: ValidationError[];
  public validationErrorMap: PropertyValidation;
  public metadata: Record<string, any>;

  constructor({
    fnName = 'Anonymous Fn',
    message = undefined,
    errorCode = ServiceErrorCode.UnknownError,
    validationErrors = [],
    metadata = {},
  }: ServiceErrorInput = {}) {
    super();

    const validationErrorMap = {} as PropertyValidation;

    if (errorCode === ServiceErrorCode.ValidationGuard) {
      validationErrors?.forEach((validationError) => {
        validationErrorMap[validationError.property as PropertyValidationKey] =
          true;
      });
    }

    this.name = errorCode;
    this.fnName = fnName;
    this.metadata = metadata;
    this.errorCode = errorCode as ServiceErrorCode;
    this.message = `[ServiceError]: ${message || errorCode}`;
    this.validationErrors = validationErrors;
    this.validationErrorMap = validationErrorMap;
  }

  getErrorMessage(defaultMessage?: { title: string; message: string }): {
    title: string;
    message: string | string[];
    metadata?: Record<string, any>;
  } {
    switch (this.errorCode) {
      case ServiceErrorCode.AiExperimentParseFailure:
        return {
          title: 'AI experiment error',
          message:
            'There was an error parsing the experiment response (parse failure). Please try again.',
        };
      case ServiceErrorCode.AiExperimentNoChoices:
        return {
          title: 'AI experiment error',
          message:
            'There was an error parsing the experiment response (no choices). Please try again.',
        };
      case ServiceErrorCode.AiExperimentNoMessageContent:
        return {
          title: 'AI experiment error',
          message:
            'There was an error parsing the experiment response (no content). Please try again.',
        };
      case ServiceErrorCode.AiExperimentUnknownMetricType:
        return {
          title: 'AI experiment error',
          message:
            'There was an error parsing the experiment response (unknown metric). Please try again.',
        };
      case ServiceErrorCode.AiExperimentGenerationFailed:
        return {
          title: 'AI experiment error',
          message:
            'There was an error generating the experiment. Please try again.',
        };
      case ServiceErrorCode.AlreadyJoined:
        return {
          title: 'Already joined',
          message: 'This user has already joined the organization.',
        };
      /**
       * Done and validated
       */
      case ServiceErrorCode.AlreadyJoinedWaitlist:
        return {
          title: 'Already joined!',
          message:
            "You've already joined the waitlist. We'll notify you when we're ready to launch.",
        };
      case ServiceErrorCode.AlreadyVerified:
        return {
          title: 'Already verified',
          message: 'This user has already verified their account.',
        };
      case ServiceErrorCode.BadRequest:
        return {
          title: 'Bad request',
          message: 'Invalid request parameters.',
        };
      case ServiceErrorCode.InsertFailure:
        return {
          title: 'Insert failure',
          message: 'Failed to insert record.',
        };
      case ServiceErrorCode.BulkInsertFailure:
        return {
          title: 'Bulk insert failure',
          message: 'Failed to bulk insert records.',
        };
      case ServiceErrorCode.DuplicateEmail:
        return {
          title: 'Duplicate email',
          message: 'Email already exists.',
        };
      case ServiceErrorCode.DuplicateMetricName:
        return {
          title: this.metadata?.title ?? 'Duplicate metric name',
          message: this.metadata?.message ?? 'Metric name already exists.',
        };
      case ServiceErrorCode.FlagNotFound:
        return {
          title: 'Flag not found',
          message: 'Flag not found.',
        };
      case ServiceErrorCode.FlagHasExperiments:
        return {
          title: 'Experiment',
          message:
            'This feature flag is linked to one or more experiments in this or another environment. To archive this flag, you must first archive the connected experiments.',
        };
      case ServiceErrorCode.MetricHasExperiments:
        return {
          title: 'Metric has experiments',
          message:
            'This metric is linked with one or more experiments. To archive this metric, you must remove it from the connected experiments.',
        };
      case ServiceErrorCode.InvalidToken:
        return {
          title: 'Invalid token',
          message: 'Invalid token.',
        };
      case ServiceErrorCode.IllegalTierAction:
        return {
          title: ServiceErrorCode.IllegalTierAction,
          message:
            "This action is not allowed given your organization's tier. Upgrade your tier to access this feature!",
          metadata: this.metadata,
        };
      case ServiceErrorCode.EmptyTokens:
        return {
          title: 'Empty tokens',
          message: 'Empty tokens.',
        };
      case ServiceErrorCode.ExperimentRunning:
        return {
          title: 'Running experiment',
          message: 'You cannot update an experiment that is currently running.',
        };
      case ServiceErrorCode.InvalidCredentials:
        return {
          title: 'Invalid credentials',
          message: 'Invalid credentials.',
        };
      case ServiceErrorCode.InvalidTimeframe:
        return {
          title: 'Invalid timeframe',
          message: 'Invalid timeframe.',
        };
      /**
       * Done and validated
       */
      case ServiceErrorCode.ForbiddenResource:
        return {
          title: 'Role restriction',
          message:
            'You do not have permission to make this change based on your role. Contact your administrator if you believe this is in error.',
        };
      case ServiceErrorCode.PasswordReuse:
        return {
          title: 'Password reuse',
          message: 'Password reuse.',
        };
      case ServiceErrorCode.StripeBillingSessionError:
        return {
          title: this.metadata?.title ?? 'Stripe billing session error',
          message: this.metadata?.message ?? 'Stripe billing session error.',
        };
      case ServiceErrorCode.StripePlanAlreadySelected:
        return {
          title: 'Plan already selected',
          message:
            'Your organization has already selected a tier plan. To update your plan, navigate to Organization > Billing.',
        };
      case ServiceErrorCode.ScreenshotBodyTagNotFound:
        return {
          title: 'Screenshot error',
          message:
            'We were unable to find the body tag in the HTML. Please try again, or provide another URL. Contact support@flagsync.com if the problem persists.',
        };
      case ServiceErrorCode.ScreenshotBoundingBoxFound:
        return {
          title: 'Screenshot error',
          message:
            'We were unable to calculate the dimensions of the HTML. Please try again, or provide another URL. Contact support@flagsync.com if the problem persists.',
        };
      case ServiceErrorCode.TransactionFailure:
        return {
          title: 'Transaction failure',
          message: 'Transaction failure.',
        };
      case ServiceErrorCode.UnknownAccount:
        return {
          title: 'Unknown account',
          message: 'Unknown account.',
        };
      case ServiceErrorCode.UnknownError:
        return {
          title: 'Uh oh! Something went wrong.',
          message:
            "We're not entirely sure what happened. Please try again, or contact support@flagsync.com if the problem persists.",
        };
      case ServiceErrorCode.UnknownEntity:
        return {
          title: 'Unknown entity',
          message:
            "Sorry, we weren't able to find the entity you're looking for.",
        };
      case ServiceErrorCode.UserOrganizationCtxNotFound:
        return {
          title: 'User organization context not found',
          message: 'User organization context not found.',
        };
      /**
       * Done and validated (but needs to be applied to all Skyline entities)
       */
      case ServiceErrorCode.UniqueViolationError:
        return {
          title: this.metadata?.title ?? 'Duplicate entity',
          message:
            this.metadata?.message ??
            'An entity with similar attributes to this already exists.',
        };
      case ServiceErrorCode.ValidationGuard: {
        const validationError = this.validationErrors[0];
        return {
          title: 'Validation error',
          message:
            Object.values(validationError.constraints ?? {}) ??
            'Unable to parse validation error',
        };
      }
      case ServiceErrorCode.EnvironmentRunningExperiments:
        return {
          title: 'Experiments are running',
          message:
            this.metadata?.message ??
            'This environment has running experiments. You must stop them before deleting the environment.',
        };
      case ServiceErrorCode.CloudflareTurnstileHttpFailure:
        return {
          title: 'Cloudflare Error',
          message:
            'Verification could not be completed due a Cloudflare error. Please try again shortly.',
        };
      case ServiceErrorCode.CloudflareTurnstile:
        return {
          title: 'Cloudflare Error',
          message:
            'We couldn’t confirm that you’re human. Please contact support@flagsync.com if the problem persists.',
        };
      case ServiceErrorCode.CloudflareTurnstileMissing:
        return {
          title: 'Cloudflare Error',
          message:
            'We couldn’t confirm that you’re human (no token). Please contact support@flagsync.com if the problem persists.',
        };
      case ServiceErrorCode.ViewAsReadOnly:
        return {
          title: 'Read-only session',
          message:
            "You're viewing as another user. This session is read-only — exit View-as to make changes.",
        };
      case ServiceErrorCode.ViewAsTargetIsGod:
        return {
          title: 'Cannot view as this user',
          message: 'You cannot view as another god-level user.',
        };
      case ServiceErrorCode.ViewAsSelf:
        return {
          title: 'Cannot view as yourself',
          message: 'Pick a different user to view as.',
        };
      default:
        return {
          title: defaultMessage?.title ?? 'Uh oh! Something went wrong.',
          message:
            defaultMessage?.message ??
            "We're not entirely sure what happened. Please try again, or contact support@flagsync.com if the problem persists.",
        };
    }
  }
}
